import { useEffect } from "react";
import { Crown, Check, Percent, Wallet } from "lucide-react";
import { formatINR, computeTieredBreakdownV2 } from "@/lib/quotationApi";

export default function Step5SelectTiers({
  data,
  tierConfigV2,
  selectedTierIds,
  onToggleTier,
  onSelectAll,
  onSelectNone,
  paymentPlans = [],
  paymentPlanId = "",
  onSelectPaymentPlan,
}) {
  const tiers = (tierConfigV2?.tiers || []).slice().sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  // Auto-select all when the list first loads and nothing is selected.
  useEffect(() => {
    if (tiers.length > 0 && selectedTierIds.length === 0) {
      onSelectAll();
    }
  }, [tiers.length]);

  const live = computeTieredBreakdownV2(data, tierConfigV2, selectedTierIds);
  const totalsById = Object.fromEntries(
    (live.list || []).map((t) => [t.id, t.breakdown.grand_total]),
  );
  const hasPlans = paymentPlans.length > 0;

  return (
    <div data-testid="step-5-select-tiers">
      <div className="mb-6">
        <div className="overline text-[#C5A880] mb-2">Step 05 · Tiers</div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold leading-tight">
          Choose tiers to quote
        </h1>
        <p className="text-white/50 mt-2 text-sm">
          Pick which pricing tiers the customer will see on the final
          quotation. Leave them all on, or narrow down to just the relevant
          options.
        </p>
      </div>

      {/* Payment plan selector (top of step) */}
      {hasPlans && (
        <div className="mb-6" data-testid="payment-plan-picker">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={14} className="text-[#C5A880]" />
            <div className="overline text-white/60">Payment plan</div>
          </div>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => onSelectPaymentPlan?.("")}
              className="w-full text-left rounded-xl border p-3 transition"
              style={{
                borderColor: !paymentPlanId
                  ? "var(--primary)"
                  : "rgba(255,255,255,0.08)",
                background: !paymentPlanId
                  ? "color-mix(in srgb, var(--primary) 10%, transparent)"
                  : "rgba(255,255,255,0.02)",
              }}
              data-testid="payment-plan-option-none"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-5 h-5 rounded-full border-2 shrink-0"
                  style={{
                    borderColor: !paymentPlanId
                      ? "var(--primary)"
                      : "rgba(255,255,255,0.25)",
                    background: !paymentPlanId
                      ? "radial-gradient(circle, var(--primary) 40%, transparent 50%)"
                      : "transparent",
                  }}
                />
                <div className="min-w-0">
                  <div className="font-display font-semibold text-[14px]">
                    No payment plan
                  </div>
                  <div className="text-[11px] text-white/45">
                    Leave payment terms off this quotation
                  </div>
                </div>
              </div>
            </button>
            {paymentPlans.map((plan) => {
              const selected = plan.id === paymentPlanId;
              return (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => onSelectPaymentPlan?.(plan.id)}
                  className="w-full text-left rounded-xl border p-3 transition"
                  style={{
                    borderColor: selected
                      ? "var(--primary)"
                      : "rgba(255,255,255,0.08)",
                    background: selected
                      ? "color-mix(in srgb, var(--primary) 10%, transparent)"
                      : "rgba(255,255,255,0.02)",
                  }}
                  data-testid={`payment-plan-option-${plan.id}`}
                  aria-pressed={selected}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-5 h-5 rounded-full border-2 shrink-0 mt-0.5"
                      style={{
                        borderColor: selected
                          ? "var(--primary)"
                          : "rgba(255,255,255,0.25)",
                        background: selected
                          ? "radial-gradient(circle, var(--primary) 40%, transparent 50%)"
                          : "transparent",
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-[14px]">
                        {plan.name}
                      </div>
                      {plan.description && (
                        <div className="text-[11px] text-white/50 mt-0.5 leading-snug">
                          {plan.description}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {plan.milestones.map((m, i) => (
                          <span
                            key={`${plan.id || "p"}-m-${i}-${m.percent}-${m.label}`}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03] text-white/70"
                          >
                            <span className="font-mono text-[#C5A880]">
                              {m.percent}%
                            </span>
                            <span className="truncate max-w-[140px]">{m.label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div
          className="overline text-white/50"
          data-testid="selected-tier-count"
        >
          {selectedTierIds.length}/{tiers.length} selected
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onSelectAll}
            className="text-[11px] px-2.5 py-1 rounded-md border border-white/10 text-white/60 hover:text-[#C5A880] hover:border-[#C5A880]/40 transition"
            data-testid="tiers-select-all"
          >
            Select all
          </button>
          <button
            onClick={onSelectNone}
            className="text-[11px] px-2.5 py-1 rounded-md border border-white/10 text-white/60 hover:text-white hover:border-white/25 transition"
            data-testid="tiers-clear-all"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="space-y-3" data-testid="tier-picker-list">
        {tiers.map((t) => {
          const selected = selectedTierIds.includes(t.id);
          const grand = totalsById[t.id];
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onToggleTier(t.id)}
              className="w-full text-left relative overflow-hidden rounded-2xl border p-4 transition"
              style={{
                borderColor: selected ? `${t.color}99` : "rgba(255,255,255,0.08)",
                background: selected
                  ? `linear-gradient(135deg, ${t.color}22, rgba(255,255,255,0.02) 70%)`
                  : "rgba(255,255,255,0.02)",
              }}
              data-testid={`tier-option-${t.id}`}
              aria-pressed={selected}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-6 h-6 rounded-md border flex-shrink-0 flex items-center justify-center transition"
                  style={{
                    borderColor: selected ? t.color : "rgba(255,255,255,0.15)",
                    background: selected ? t.color : "transparent",
                  }}
                >
                  {selected && (
                    <Check
                      size={14}
                      style={{ color: "#0A0C10" }}
                      strokeWidth={3}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {t.is_base && (
                      <Crown size={13} style={{ color: t.color }} />
                    )}
                    <div
                      className="font-display font-semibold text-[16px]"
                      style={{ color: t.color }}
                    >
                      {t.name}
                    </div>
                  </div>
                  {t.description && (
                    <p className="text-[12.5px] text-white/60 leading-snug">
                      {t.description}
                    </p>
                  )}
                  {t.modular_multiplier != null && (
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-white/40">
                      <span>
                        Modular <span className="text-white/70">×{t.modular_multiplier}</span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div
                    className="font-display font-bold text-[17px] leading-none"
                    style={{ color: selected ? t.color : "rgba(255,255,255,0.5)" }}
                    data-testid={`tier-option-total-${t.id}`}
                  >
                    {formatINR(grand || 0)}
                  </div>
                  <div className="text-[10px] text-white/35 mt-0.5">
                    grand total
                  </div>
                </div>
              </div>
            </button>
          );
        })}
        {tiers.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center text-white/50 text-sm">
            No tiers configured yet. Go to{" "}
            <span className="text-[#C5A880]">Admin → Tiers</span> to add some.
          </div>
        )}
      </div>

      {selectedTierIds.length === 0 && tiers.length > 0 && (
        <p
          className="text-[12px] text-amber-400/90 mt-4"
          data-testid="tiers-none-selected-warning"
        >
          Select at least one tier to continue.
        </p>
      )}
    </div>
  );
}
